import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/types/database.types";

export type VoiceAgent = Tables<"voice_agents">;
export type VoiceCall = Tables<"voice_calls">;

export interface VoiceAgentCreateData {
  title: string;
  agent_name: string;
  description?: string;
  organization_id: string;
  created_by: string;
  instructions?: string;
  first_message?: string;
  end_call_message?: string;
  ai_temperature?: number;
  voice_provider?: string;
  voice_id?: string;
  max_duration_seconds?: number;
  silence_timeout_seconds?: number;
  first_message_interruptions_enabled?: boolean;
  background_sound_enabled?: boolean;
  voicemail_message?: string;
  avatar_url?: string;
  behaviors?: string[];
  form_fields?: any[];
  phone_number?: string;
  twilio_account_sid?: string;
  twilio_auth_token?: string;
  notification_email?: string;
  notification_webhook_url?: string;
  template_source_id?: string;
}

export interface VoiceAgentStats {
  total_calls: number;
  calls_today: number;
  average_duration_seconds: number;
  end_reasons: Record<string, number>;
  total_cost: number;
}

// Cache interface for voice agents
interface VoiceAgentCache {
  data: VoiceAgent[];
  timestamp: number;
  ttl: number;
}

// In-memory cache for voice agents
class VoiceAgentCacheManager {
  private cache: VoiceAgentCache | null = null;
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

  set(data: VoiceAgent[], ttl: number = this.DEFAULT_TTL): void {
    this.cache = {
      data,
      timestamp: Date.now(),
      ttl,
    };
  }

  get(): VoiceAgent[] | null {
    if (!this.cache) return null;

    // Check if cache has expired
    if (Date.now() - this.cache.timestamp > this.cache.ttl) {
      this.cache = null;
      return null;
    }

    return this.cache.data;
  }

  clear(): void {
    this.cache = null;
  }
}

// Create a singleton instance
const voiceAgentCache = new VoiceAgentCacheManager();

export const voiceAgentService = {
  // Get all voice agents for the organization (excluding archived by default)
  async getVoiceAgents(includeArchived: boolean = false): Promise<{
    data: VoiceAgent[] | null;
    error: Error | null;
  }> {
    try {
      // Check cache first (only use cache for non-archived requests)
      if (!includeArchived) {
        const cachedData = voiceAgentCache.get();
        if (cachedData !== null) {
          console.log("[voiceAgentService] Using cached voice agents");
          return { data: cachedData, error: null };
        }
      }

      console.log(
        "[voiceAgentService] Cache miss, fetching from edge function",
      );
      const { data, error } = await supabase.functions.invoke(
        "voice-agent-service",
        {
          method: "GET",
          ...(includeArchived && { headers: { "include-archived": "true" } }),
        },
      );

      if (error) throw error;

      const voiceAgents = data.data as VoiceAgent[];

      // Cache the result (only cache non-archived results)
      if (voiceAgents && !includeArchived) {
        voiceAgentCache.set(voiceAgents);
      }

      return { data: voiceAgents, error: null };
    } catch (error) {
      console.error("Failed to fetch voice agents:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Get only archived voice agents
  async getArchivedVoiceAgents(): Promise<{
    data: VoiceAgent[] | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("voice_agents")
        .select("*")
        .eq("archived", true)
        .order("updated_at", { ascending: false });

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error("Failed to fetch archived voice agents:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Archive a voice agent (soft delete)
  async archiveVoiceAgent(id: string): Promise<{
    success: boolean;
    error: Error | null;
  }> {
    try {
      const { error } = await supabase
        .from("voice_agents")
        .update({ archived: true })
        .eq("id", id);

      if (error) throw error;

      // Clear cache since a voice agent was archived
      this.clearCache();

      return { success: true, error: null };
    } catch (error) {
      console.error("Failed to archive voice agent:", error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Unarchive a voice agent
  async unarchiveVoiceAgent(id: string): Promise<{
    success: boolean;
    error: Error | null;
  }> {
    try {
      const { error } = await supabase
        .from("voice_agents")
        .update({ archived: false })
        .eq("id", id);

      if (error) throw error;

      // Clear cache since a voice agent was unarchived
      this.clearCache();

      return { success: true, error: null };
    } catch (error) {
      console.error("Failed to unarchive voice agent:", error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Clear the cache - useful when voice agents might have been updated
  clearCache(): void {
    voiceAgentCache.clear();
    console.log("[voiceAgentService] Voice agent cache cleared");
  },

  // Get a single voice agent
  async getVoiceAgent(id: string): Promise<{
    data: VoiceAgent | null;
    error: Error | null;
  }> {
    try {
      // Use direct Supabase call instead of edge function
      const { data, error } = await supabase
        .from("voice_agents")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch voice agent:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Create a new voice agent
  async createVoiceAgent(agentData: VoiceAgentCreateData): Promise<{
    data: VoiceAgent | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke(
        "voice-agent-service",
        {
          method: "POST",
          body: agentData,
        },
      );

      if (error) throw error;

      // Clear cache since a new voice agent was created
      this.clearCache();

      return { data: data.data, error: null };
    } catch (error) {
      console.error("Failed to create voice agent:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Update a voice agent
  async updateVoiceAgent(
    id: string,
    updates: Partial<VoiceAgentCreateData>,
  ): Promise<{
    data: VoiceAgent | null;
    error: Error | null;
  }> {
    try {
      // Use direct Supabase call instead of edge function for updates
      const { error } = await supabase
        .from("voice_agents")
        .update(updates)
        .eq("id", id);

      if (error) throw error;

      // Fetch the updated agent with safe columns only
      const { data, error: fetchError } = await supabase
        .from("voice_agents")
        .select("*, organizations(name)")
        .eq("id", id)
        .single();

      if (fetchError) throw fetchError;

      // Clear cache since a voice agent was updated
      this.clearCache();

      return { data, error: null };
    } catch (error) {
      console.error("Failed to update voice agent:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Publish voice agent to VAPI (first time)
  async publishVoiceAgentToVapi(id: string): Promise<{
    data: VoiceAgent | null;
    error: Error | null;
  }> {
    try {
      console.log("🚀 Publishing voice agent to VAPI for agent:", id);
      const { data, error } = await supabase.functions.invoke(
        `voice-agent-service/${id}?t=${Date.now()}`,
        {
          method: "PUT",
        },
      );

      if (error) throw error;

      // Clear cache since a voice agent was updated
      this.clearCache();

      return { data: data.data, error: null };
    } catch (error) {
      console.error("Failed to publish voice agent to VAPI:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Update voice agent in both database and VAPI
  async updateVoiceAgentInVapi(
    id: string,
    updates: Partial<VoiceAgentCreateData>,
  ): Promise<{
    data: VoiceAgent | null;
    error: Error | null;
  }> {
    try {
      console.log(
        "🔄 Calling updateVoiceAgentInVapi with PATCH method for agent:",
        id,
      );
      const { data, error } = await supabase.functions.invoke(
        `voice-agent-service/${id}?t=${Date.now()}`,
        {
          method: "PATCH",
          body: updates,
        },
      );

      if (error) throw error;

      // Clear cache since a voice agent was updated
      this.clearCache();

      return { data: data.data, error: null };
    } catch (error) {
      console.error("Failed to update voice agent in VAPI:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Delete a voice agent
  async deleteVoiceAgent(id: string): Promise<{
    success: boolean;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke(
        `voice-agent-service/${id}`,
        {
          method: "DELETE",
        },
      );

      if (error) throw error;

      // Clear cache since a voice agent was deleted
      this.clearCache();

      return { success: true, error: null };
    } catch (error) {
      console.error("Failed to delete voice agent:", error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Get voice calls for an agent or organization
  async getVoiceCalls(params?: {
    voiceAgentId?: string;
    organizationId?: string;
    limit?: number;
    offset?: number;
    startDate?: string;
    endDate?: string;
    endReason?: string;
  }): Promise<{
    data: VoiceCall[] | null;
    count: number;
    analytics: VoiceAgentStats | null;
    error: Error | null;
  }> {
    try {
      const searchParams = new URLSearchParams();

      if (params?.voiceAgentId)
        searchParams.set("voice_agent_id", params.voiceAgentId);
      if (params?.organizationId)
        searchParams.set("organization_id", params.organizationId);
      if (params?.limit) searchParams.set("limit", params.limit.toString());
      if (params?.offset) searchParams.set("offset", params.offset.toString());
      if (params?.startDate) searchParams.set("start_date", params.startDate);
      if (params?.endDate) searchParams.set("end_date", params.endDate);
      if (params?.endReason) searchParams.set("end_reason", params.endReason);

      const url = searchParams.toString()
        ? `voice-calls-service?${searchParams.toString()}`
        : "voice-calls-service";

      const { data, error } = await supabase.functions.invoke(url, {
        method: "GET",
      });

      if (error) throw error;

      return {
        data: data.data,
        count: data.count || 0,
        analytics: data.analytics,
        error: null,
      };
    } catch (error) {
      console.error("Failed to fetch voice calls:", error);
      return {
        data: null,
        count: 0,
        analytics: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Get organization-level voice agent stats (optimized single call)
  async getOrganizationVoiceStats(organizationId: string): Promise<{
    analytics: VoiceAgentStats | null;
    error: Error | null;
  }> {
    try {
      const { analytics, error } = await this.getVoiceCalls({
        organizationId,
        limit: 1, // We only need analytics, not the actual call data
      });

      if (error) throw error;

      return { analytics, error: null };
    } catch (error) {
      console.error("Failed to fetch organization voice stats:", error);
      return {
        analytics: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Batch fetch stats for multiple voice agents (optimized approach)
  async getVoiceAgentStatsOptimized(
    agents: VoiceAgent[],
    organizationId: string,
  ): Promise<{
    stats: Record<string, VoiceAgentStats>;
    error: Error | null;
  }> {
    try {
      // For small numbers of agents, parallel individual calls may be faster
      // For larger numbers, we might want to implement server-side batch processing
      const BATCH_THRESHOLD = 5;

      if (agents.length <= BATCH_THRESHOLD) {
        // Use parallel individual calls for small numbers
        const statsPromises = agents.map(async (agent) => {
          const { analytics } = await this.getVoiceCalls({
            voiceAgentId: agent.id,
            limit: 1,
          });
          return { agentId: agent.id, stats: analytics };
        });

        const statsResults = await Promise.all(statsPromises);
        const statsMap = statsResults.reduce(
          (acc, { agentId, stats }) => {
            if (stats) {
              acc[agentId] = stats;
            }
            return acc;
          },
          {} as Record<string, VoiceAgentStats>,
        );

        return { stats: statsMap, error: null };
      } else {
        // For larger numbers, fall back to organization-level stats
        // This is a compromise - we get overall stats but lose per-agent granularity
        const { analytics, error } =
          await this.getOrganizationVoiceStats(organizationId);

        if (error) throw error;

        // Apply the same stats to all agents (not ideal but better than many calls)
        const statsMap = agents.reduce(
          (acc, agent) => {
            if (analytics) {
              acc[agent.id] = analytics;
            }
            return acc;
          },
          {} as Record<string, VoiceAgentStats>,
        );

        return { stats: statsMap, error: null };
      }
    } catch (error) {
      console.error("Failed to fetch optimized voice agent stats:", error);
      return {
        stats: {},
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Get a single voice call
  async getVoiceCall(id: string): Promise<{
    data: VoiceCall | null;
    vapiDetails: any | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke(
        `voice-calls-service/${id}`,
        {
          method: "GET",
        },
      );

      if (error) throw error;

      return {
        data: data.data,
        vapiDetails: data.vapi_details,
        error: null,
      };
    } catch (error) {
      console.error("Failed to fetch voice call:", error);
      return {
        data: null,
        vapiDetails: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Connect Twilio phone number to voice agent
  async connectTwilioPhoneNumber(
    agentId: string,
    phoneNumber: string,
    twilioAccountSid: string,
    twilioAuthToken: string,
  ): Promise<{
    data: {
      voice_agent: VoiceAgent;
      phone_number: string;
      phone_number_id: string;
      vapi_details: any;
    } | null;
    error: Error | null;
  }> {
    try {
      // Call Express route only (server will create in Vapi and persist DB atomically)
      console.log(
        "📞 [connectTwilioPhoneNumber] Creating Vapi phone number via Express route (server persists DB)...",
        {
          endpoint: "/api/voice-phone-service",
          agentId,
          phoneNumber,
        },
      );
      // Build absolute URL for clear browser logs
      const createUrl =
        typeof window !== "undefined"
          ? `${window.location.origin}/api/voice-phone-service`
          : `/api/voice-phone-service`;
      console.log(
        "🌐 [connectTwilioPhoneNumber] Step 2 request URL:",
        createUrl,
      );
      const createResp = await fetch(createUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          voice_agent_id: agentId,
          provider: "twilio",
          number: phoneNumber,
          twilioAccountSid: twilioAccountSid,
          twilioAuthToken: twilioAuthToken,
        }),
      });

      console.log("📞 [connectTwilioPhoneNumber] Express response received", {
        status: createResp.status,
        ok: createResp.ok,
      });

      if (!createResp.ok) {
        let info: any = null;
        try {
          info = await createResp.json();
          console.error(
            "❌ [connectTwilioPhoneNumber] Step 2 failed - Express error response:",
            info,
          );
        } catch (parseError) {
          console.error(
            "❌ [connectTwilioPhoneNumber] Step 2 failed - Could not parse error response",
          );
        }
        const stage = info?.stage ? ` [${info.stage}]` : "";
        const msg = info?.error || `HTTP ${createResp.status}`;
        throw new Error(`Voice phone create failed${stage}: ${msg}`);
      }

      const vapiResponse = await createResp.json();
      console.log(
        "✅ [connectTwilioPhoneNumber] Vapi phone number created & DB persisted on server",
        vapiResponse,
      );

      return {
        data: {
          voice_agent: vapiResponse?.data || null,
          phone_number: phoneNumber,
          phone_number_id:
            vapiResponse?.phone_number?.id || vapiResponse?.id || null,
          vapi_details: vapiResponse?.phone_number || vapiResponse,
        },
        error: null,
      };
    } catch (error) {
      console.error("❌ [connectTwilioPhoneNumber] ERROR:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // Disconnect phone number from voice agent
  async disconnectPhoneNumber(agentId: string): Promise<{
    data: VoiceAgent | null;
    vapiDeleteSuccess?: boolean;
    vapiError?: string;
    message?: string;
    error: Error | null;
  }> {
    try {
      console.log("🔌 Disconnecting phone number for agent:", agentId);

      const deleteUrl =
        typeof window !== "undefined"
          ? `${window.location.origin}/api/voice-phone-service`
          : `/api/voice-phone-service`;
      console.log("🌐 [disconnectPhoneNumber] Request URL:", deleteUrl);
      const resp = await fetch(deleteUrl, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ voice_agent_id: agentId }),
      });
      if (!resp.ok) {
        let info: any = null;
        try {
          info = await resp.json();
        } catch {}
        const stage = info?.stage ? ` [${info.stage}]` : "";
        const msg = info?.error || `HTTP ${resp.status}`;
        throw new Error(`Voice phone disconnect failed${stage}: ${msg}`);
      }
      const respData = await resp.json();

      console.log("✅ Phone number disconnection completed");

      // Log the result based on VAPI success/failure
      if (respData.vapiDeleteSuccess) {
        console.log("✅ VAPI deletion successful");
      } else {
        console.warn("⚠️ VAPI deletion failed:", respData.vapiError);
        console.log("✅ Database cleanup completed successfully");
      }

      return {
        data: respData.data,
        vapiDeleteSuccess: respData.vapiDeleteSuccess,
        vapiError: respData.vapiError,
        message: respData.message,
        error: null,
      };
    } catch (error) {
      console.error("Failed to disconnect phone number:", error);
      return {
        data: null,
        vapiDeleteSuccess: false,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },
};
